import { Button } from "@/components/atoms/buttons";
import { MAX_VISIBLE_PAGES } from "@/components/organisms/cart-products";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

type PaginationProps = {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  startIndex: number;
  endIndex: number;
  locale: Locales;
  goToPage: (page: number) => void;
  totalPages: number;
  showPaginationLabels?: boolean;
};

const Pagination = ({
  currentPage,
  totalItems,
  locale,
  goToPage,
  goToPrevPage,
  goToNextPage,
  startIndex,
  totalPages,
  endIndex,
  showPaginationLabels = true,
}: PaginationProps) => {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= MAX_VISIBLE_PAGES) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      // Calculate range around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      // Add ellipsis if needed
      if (start > 2) {
        pages.push("...");
      }

      // Add pages around current page
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pages.push("...");
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const totalPageNumbers = getPageNumbers();

  return (
    <nav className="flex flex-col items-center space-y-4 pt-6" aria-label={__("Pagination", locale)}>
      <div className="text-dark-primary text-sm font-medium">
        {__("Showing", locale)} {startIndex + 1}-{endIndex} {__("of", locale)} {totalItems} {__("items", locale)}
      </div>
      <ul className={cn("flex items-center gap-2", totalPageNumbers.length >= 7 && "max-md:flex-col")}>
        <li className={cn(totalPageNumbers.length >= 7 && "hidden md:block")}>
          <Button
            variant="secondary"
            size="sm"
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className="flex items-center space-x-1"
            aria-label={__("Go to previous page", locale)}
          >
            <ChevronLeftIcon size={16} />
            {showPaginationLabels && <span className="hidden sm:inline">{__("Previous", locale)}</span>}
          </Button>
        </li>
        <div className="flex items-center gap-1">
          {totalPageNumbers.map((page, index) => (
            <li key={index}>
              {page === "..." ? (
                <span className="px-2 py-1 text-gray-500" aria-hidden="true">
                  ...
                </span>
              ) : (
                <Button
                  variant={currentPage === page ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => goToPage(page as number)}
                  className="min-w-[2.5rem]"
                  aria-current={currentPage === page ? "page" : undefined}
                  aria-label={__("Go to page", locale) + ` ${page}`}
                >
                  {page}
                </Button>
              )}
            </li>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <li className={cn("hidden", totalPageNumbers.length >= 7 && "block md:hidden")}>
            <Button
              variant="secondary"
              size="sm"
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className="flex items-center space-x-1"
              aria-label={__("Go to previous page", locale)}
            >
              <ChevronLeftIcon size={16} />
              <span className="hidden sm:inline">{__("Previous", locale)}</span>
            </Button>
          </li>
          <li>
            <Button
              variant="secondary"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center space-x-1"
              aria-label={__("Go to next page", locale)}
            >
              {showPaginationLabels && <span className="hidden sm:inline">{__("Next", locale)}</span>}
              <ChevronRightIcon size={16} />
            </Button>
          </li>
        </div>
      </ul>
    </nav>
  );
};

export default Pagination;
